import { Stack, Group, Text, Grid, Card, Badge, Progress } from '../../../../components/ui';
import { IconCheck, IconX, IconDatabase } from '@tabler/icons-react';
import type { RedisOverviewTabProps } from '../types';

export function RedisOverviewTab({ health, statistics }: RedisOverviewTabProps) {
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Stack gap="md">
      {/* Health Status */}
      <Card>
        <Stack gap="sm">
          <Group gap="sm">
            <IconDatabase size={20} />
            <Text size="md" fw={600}>
              Connection Health
            </Text>
          </Group>
          {health && (
            <Grid>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Status
                </Text>
                <Badge
                  color={health.isHealthy ? 'green' : 'red'}
                  leftSection={health.isHealthy ? <IconCheck size={14} /> : <IconX size={14} />}
                >
                  {health.connectionState}
                </Badge>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Latency
                </Text>
                <Text fw={500}>{health.latencyMs.toFixed(2)} ms</Text>
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Card>

      {/* Statistics */}
      {statistics && (
        <>
          <Grid>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card variant="gradient" gradientVariant="primary" padding="md">
                <Stack gap="xs">
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Hit Rate
                  </Text>
                  <Text size="xl" fw={700} c="white">
                    {statistics.hitRate.toFixed(1)}%
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card variant="gradient" gradientVariant="success" padding="md">
                <Stack gap="xs">
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Total Keys
                  </Text>
                  <Text size="xl" fw={700} c="white">
                    {statistics.itemCount}
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card variant="gradient" gradientVariant="info" padding="md">
                <Stack gap="xs">
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Memory Usage
                  </Text>
                  <Text size="xl" fw={700} c="white">
                    {formatBytes(statistics.memoryUsageBytes)}
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
              <Card variant="gradient" gradientVariant="secondary" padding="md">
                <Stack gap="xs">
                  <Text size="sm" c="white" style={{ opacity: 0.9 }}>
                    Operations
                  </Text>
                  <Text size="xl" fw={700} c="white">
                    {statistics.totalOperations}
                  </Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>

          <Card>
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Cache Performance
              </Text>
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="xs">Hits: {statistics.hits}</Text>
                  <Text size="xs">Misses: {statistics.misses}</Text>
                </Group>
                <Progress value={statistics.hitRate} color="green" size="lg" />
              </div>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}
